import type { Database, Tables } from './database.types.js';

export type Game = Tables<'games'>;
export type Task = Tables<'tasks'>;
export type Label = Tables<'labels'>;
export type TaskComment = Tables<'task_comments'>;
export type Activity = Tables<'activity'>;
export type TaskStatus = Database['public']['Enums']['task_status'];
export type GameStatus = Database['public']['Enums']['game_status'];

/** Board columns, in order. */
export const TASK_STATUSES: readonly { id: TaskStatus; name: string }[] = [
  { id: 'backlog', name: 'Backlog' },
  { id: 'todo', name: 'To do' },
  { id: 'in_progress', name: 'In progress' },
  { id: 'review', name: 'Review' },
  { id: 'done', name: 'Done' },
];

export const TASK_STATUS_IDS = TASK_STATUSES.map((s) => s.id) as [TaskStatus, ...TaskStatus[]];

export function taskStatusName(status: TaskStatus): string {
  return TASK_STATUSES.find((s) => s.id === status)?.name ?? status;
}

/** 1 urgent .. 4 none, like Todoist's p1..p4. */
export const PRIORITIES: readonly { id: 1 | 2 | 3 | 4; name: string }[] = [
  { id: 1, name: 'Urgent' },
  { id: 2, name: 'High' },
  { id: 3, name: 'Medium' },
  { id: 4, name: 'None' },
];

export function priorityName(priority: number): string {
  return PRIORITIES.find((p) => p.id === priority)?.name ?? 'None';
}

export const GAME_STATUSES: readonly { id: GameStatus; name: string }[] = [
  { id: 'idea', name: 'Idea' },
  { id: 'pre-production', name: 'Pre-production' },
  { id: 'production', name: 'Production' },
  { id: 'released', name: 'Released' },
];

export const GAME_STATUS_IDS = GAME_STATUSES.map((s) => s.id) as [GameStatus, ...GameStatus[]];

/** "Moon Rush!" -> "moon-rush". Matches the games.slug check constraint. */
export function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** A calendar date as yyyy-mm-dd, in local time. */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Adds whole days to a yyyy-mm-dd date. */
export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return toIsoDate(new Date(y, m - 1, d + days));
}
